import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Check, ArrowRight, ShieldCheck, Leaf, MessageCircle, Facebook, ShoppingBag } from "lucide-react";
import { FACEBOOK_URL, MESSENGER_URL } from "@/constants/content";
import { useSiteContent } from "@/hooks/useSiteContent";
import { FOURLIFE } from "@/constants/testIds";

export default function FourLife() {
  const { content } = useSiteContent();
  const FOURLIFE_PRODUCTS = content.fourlife;
  const HIGHLIGHT_PACKAGES = content.highlights || [];
  return (
    <div data-testid={FOURLIFE.page}>
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-14 md:pt-20 pb-10 md:pb-14">
        <div className="max-w-3xl">
          <span className="text-[11px] uppercase tracking-[0.3em] text-[#A88A2B]">4Life Megoldások</span>
          <h1 className="font-serif-display text-5xl md:text-6xl font-light text-[#1A2825] mt-4 leading-[1.04]">
            Belülről építsd <em className="not-italic text-[#A88A2B]">újra</em> magad.
          </h1>
          <p className="mt-6 text-lg text-[#3A4D4A] font-light leading-relaxed">
            Bevizsgált, prémium minőségű természetes csomagok családoknak, sportolóknak és minden
            korosztálynak. Mindennapi vitalitás, ízületvédelem, és gyorsabb regeneráció — olyan
            megoldásokkal, amelyeket én magam is használok.
          </p>
          <div className="mt-6 flex items-center gap-3 text-xs uppercase tracking-wider text-[#758C88]">
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-[#2A9D8F]" /> Bevizsgált alapanyagok</span>
            <span className="inline-flex items-center gap-1.5"><Leaf className="h-3.5 w-3.5 text-[#2A9D8F]" /> Természetes alapokon</span>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-16 md:pb-24">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {FOURLIFE_PRODUCTS.map((p, i) => (
            <motion.article
              key={p.key}
              id={p.key}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, delay: i * 0.1 }}
              data-testid={FOURLIFE.card(p.key)}
              className="relative bg-white rounded-[2rem] border border-[#D4AF37]/30 overflow-hidden flex flex-col shadow-[0_8px_32px_rgba(212,175,55,0.08)]"
            >
              <div className="h-2" style={{ background: `linear-gradient(90deg, ${p.accent}, ${p.accent}80)` }} />
              <div className="p-7 md:p-8 flex-1 flex flex-col">
                <div className="text-[10px] uppercase tracking-[0.3em]" style={{ color: p.accent }}>4Life</div>
                <h2 className="font-serif-display text-2xl md:text-[28px] text-[#1A2825] mt-2 leading-snug">{p.title}</h2>
                <p className="mt-2 text-sm italic" style={{ color: p.accent }}>{p.tagline}</p>
                <p className="mt-5 text-[#3A4D4A] font-light leading-relaxed text-[15px]">{p.description}</p>
                <ul className="mt-5 space-y-2 text-sm text-[#3A4D4A] font-light">
                  {p.highlights.map((h) => (
                    <li key={h} className="flex items-start gap-2">
                      <Check className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: p.accent }} />
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href={MESSENGER_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={FOURLIFE.detailsBtn(p.key)}
                  className="mt-7 inline-flex items-center justify-center gap-2 text-white px-6 py-3 rounded-full font-medium tracking-wide transition-all shadow-md hover:-translate-y-0.5"
                  style={{ background: p.accent }}
                >
                  <MessageCircle className="h-4 w-4" />
                  Részletek a Messengeren
                </a>
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      {/* Kiemelt csomag-tájékoztatók */}
      {HIGHLIGHT_PACKAGES.map((pkg) => (
        <section
          key={pkg.key}
          id={`hl-${pkg.key}`}
          className="max-w-7xl mx-auto px-6 md:px-12 pb-16 md:pb-20 scroll-mt-24"
        >
          <div
            data-testid={`fourlife-highlight-${pkg.key}`}
            className="bg-[#F6F3EB]/80 rounded-[2.5rem] p-8 md:p-12"
            style={{ border: `1px solid ${pkg.accent}40` }}
          >
            {pkg.cover && (
              <div className="mb-7 rounded-[1.75rem] overflow-hidden h-44 md:h-60">
                <img
                  src={pkg.cover}
                  alt={pkg.title}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            <span className="text-[11px] uppercase tracking-[0.3em]" style={{ color: pkg.accent }}>
              {pkg.badge}
            </span>
            <h2 className="font-serif-display text-3xl md:text-4xl text-[#1A2825] mt-3 leading-tight">
              {pkg.title}
            </h2>
            <p className="mt-4 text-[#3A4D4A] font-light leading-relaxed max-w-3xl">{pkg.intro}</p>

            <div
              className={`mt-8 grid grid-cols-1 gap-5 ${
                (pkg.products || []).length >= 4 ? "md:grid-cols-2 lg:grid-cols-4" : "md:grid-cols-3"
              }`}
            >
              {(pkg.products || []).map((k) => (
                <div
                  key={k.name}
                  data-testid={`highlight-product-${pkg.key}-${k.name}`}
                  className="bg-white rounded-[1.5rem] border border-[#EAE5D9] p-6"
                >
                  <div className="flex items-center gap-2">
                    <Leaf className="h-4 w-4" style={{ color: pkg.accent }} />
                    <h3 className="font-serif-display text-lg leading-snug text-[#1A2825]">{k.name}</h3>
                  </div>
                  <p className="mt-3 text-sm text-[#3A4D4A] font-light leading-relaxed">{k.text}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <a
                href={pkg.orderUrl || MESSENGER_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`highlight-order-btn-${pkg.key}`}
                className="inline-flex items-center justify-center gap-2 text-white px-7 py-3.5 rounded-full font-medium tracking-wide transition-all hover:-translate-y-0.5"
                style={{ background: pkg.accent, boxShadow: `0 10px 24px ${pkg.accent}4D` }}
              >
                {pkg.orderUrl ? (
                  <>
                    <ShoppingBag className="h-4 w-4" /> Megrendelem a 4Life boltban
                  </>
                ) : (
                  <>
                    <MessageCircle className="h-4 w-4" /> Bővebb infó vagy rendelés
                  </>
                )}
              </a>
              <Link
                to={pkg.articleTo}
                data-testid={`highlight-article-link-${pkg.key}`}
                className="inline-flex items-center gap-2 text-[#1C6B61] hover:text-[#2A9D8F] text-sm font-medium transition-colors"
              >
                {pkg.articleLabel} <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      ))}

      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-20 md:pb-28">
        <div className="bg-[#F6F3EB]/80 border border-[#D4AF37]/30 rounded-[2.5rem] p-10 md:p-14 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <h2 className="font-serif-display text-3xl md:text-4xl text-[#1A2825] leading-tight">
              Nem vagy biztos benne, melyik csomag illik hozzád?
            </h2>
            <p className="mt-3 text-[#3A4D4A] font-light leading-relaxed max-w-2xl">
              Írj egy emailt vagy üzenj Facebookon — összerakjuk neked a legjobb kombinációt
              családodra, mozgásformádra vagy életszakaszodra szabva.
            </p>
          </div>
          <div className="md:col-span-4 flex flex-col gap-3">
            <a href={MESSENGER_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#1877F2] hover:bg-[#0e5fcc] text-white px-6 py-3.5 rounded-full font-medium tracking-wide transition-all">
              <MessageCircle className="h-4 w-4" /> Üzenek Messengeren
            </a>
            <a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-white border border-[#1877F2]/30 text-[#1877F2] hover:border-[#1877F2] px-6 py-3.5 rounded-full font-medium tracking-wide transition-all">
              <Facebook className="h-4 w-4" /> Megnézem a Facebookot
            </a>
            <Link to="/#magnetek" className="inline-flex items-center justify-center gap-2 text-sm text-[#3A4D4A] hover:text-[#A88A2B]">
              Ingyenes 4Life Sport útmutató →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
